import { InteractionRequiredAuthError } from '@azure/msal-browser';
import {
    API_SCOPES,
    AuthenticationRedirectInProgressError,
    isRecoverableAuthError,
    msalInstance,
    recoverFromStaleCache,
    startLoginRedirect,
    startTokenRedirect,
} from './msal';

export class ApiError extends Error {
    readonly status: number;
    readonly body: unknown;

    constructor(status: number, body: unknown, message: string) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}

export async function acquireAccessToken(): Promise<string> {
    const account = msalInstance.getActiveAccount();
    if (!account) {
        await startLoginRedirect();
        throw new AuthenticationRedirectInProgressError();
    }

    try {
        const result = await msalInstance.acquireTokenSilent({ scopes: [...API_SCOPES], account });
        return result.accessToken;
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            await startTokenRedirect(account);
            throw new AuthenticationRedirectInProgressError();
        }
        if (isRecoverableAuthError(error) && recoverFromStaleCache()) {
            throw new AuthenticationRedirectInProgressError('Recovering from stale authentication state');
        }
        throw error;
    }
}

export interface ApiFetchOptions extends Omit<RequestInit, 'headers'> {
    headers?: HeadersInit;
}

export async function apiFetch(url: string, options: ApiFetchOptions = {}): Promise<Response> {
    const token = await acquireAccessToken();

    const headers = new Headers(options.headers);
    headers.set('Authorization', `Bearer ${token}`);

    // Browser sets Content-Type with the right boundary for FormData / URLSearchParams / Blob.
    const body = options.body;
    const bodyNeedsJsonHeader =
        body !== undefined &&
        body !== null &&
        !(typeof FormData !== 'undefined' && body instanceof FormData) &&
        !(typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) &&
        !(typeof Blob !== 'undefined' && body instanceof Blob);
    if (bodyNeedsJsonHeader && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
        msalInstance.setActiveAccount(null);
        await startTokenRedirect(msalInstance.getAllAccounts()[0] ?? undefined);
        throw new AuthenticationRedirectInProgressError('Token rejected; reauthentication is in progress');
    }

    return response;
}
